import type { SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input, output } from '@angular/core';

import moment from 'moment-timezone';
import { Subject } from 'rxjs';

import type { CurrentUser } from '@core/account-types';
import { TranslatePipe } from '@core/i18n-legacy';
import { getFormattedTimezone } from '@core/time-legacy';
import {
  ButtonComponent,
  IconButtonComponent,
  RtTooltip,
  IconComponent,
} from '@core/user-interface-legacy';

export function isToday(...dates: moment.Moment[]): boolean {
  const today = moment();
  const todayDate = today.date();
  const todayMonth = today.month();
  const todayYear = today.year();
  return dates.every(
    date => date.month() === todayMonth && date.date() === todayDate && date.year() === todayYear,
  );
}

export function isTomorrow(...dates: moment.Moment[]): boolean {
  const tomorrow = moment().add(1, 'day');
  const tomorrowDate = tomorrow.date();
  const tomorrowMonth = tomorrow.month();
  const tomorrowYear = tomorrow.year();
  return dates.every(
    date =>
      date.month() === tomorrowMonth &&
      date.date() === tomorrowDate &&
      date.year() === tomorrowYear,
  );
}

export function isOtherYear(...dates: moment.Moment[]): boolean {
  const year = moment().year();
  return dates.every(date => date.year() !== year);
}

export type SendAtTiming = 'today' | 'tomorrow' | 'after-tomorrow';

@Component({
  selector: 'rt-mailbox-thread-message-schedule-actions',
  templateUrl: './mailbox-thread-message-schedule-actions.component.html',
  styleUrls: ['./mailbox-thread-message-schedule-actions.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, RtTooltip, IconButtonComponent, ButtonComponent, TranslatePipe],
})
export class MailboxThreadMessageScheduleActionsComponent {
  private readonly afterTomorrowFormat: string = 'D MMM LT';
  private readonly otherYearFormat: string = 'll LT';
  private readonly todayOrTomorrowFormat: string = 'LT';

  @Input() public sendAt: string;
  @Input() public sendAtTimezone: string;
  @Input() public currentAdmin: CurrentUser;

  public edit = output<void>();
  public delete = output<void>();

  public localizedDate: {
    simple: string;
    full: string;
  };

  public timezoneString: string;
  public showTimezone: boolean;

  public sendAtTiming: SendAtTiming;

  private ngOnDestroy$ = new Subject<void>();

  public ngOnChanges(changes: SimpleChanges): void {
    if ('sendAt' in changes || 'sendAtTimezone' in changes) {
      const sendAt = moment(this.sendAt).tz(this.currentAdmin.timezone);

      const { timing, simple, full } = this.getHumanReadableDate(sendAt);

      this.localizedDate = {
        simple: simple!,
        full,
      };

      this.sendAtTiming = timing;

      if (this.sendAtTimezone && this.sendAtTimezone !== this.currentAdmin.timezone) {
        const sendAtWithTimezone = moment(this.sendAt).tz(this.sendAtTimezone);
        const dayChanged = sendAt.date() !== sendAtWithTimezone.date();
        const format = dayChanged ? this.afterTomorrowFormat : this.todayOrTomorrowFormat;
        this.timezoneString = `${sendAtWithTimezone.format(format)} ${getFormattedTimezone(
          this.sendAtTimezone,
        )}`;
        this.showTimezone = true;
      } else {
        this.showTimezone = false;
      }
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private getHumanReadableDate(date: moment.Moment): {
    simple?: string;
    full: string;
    timing: SendAtTiming;
  } {
    if (isOtherYear(date)) {
      return {
        timing: 'after-tomorrow',
        full: date.format(this.otherYearFormat),
      };
    }

    if (isToday(date)) {
      return {
        timing: 'today',
        simple: date.format(this.todayOrTomorrowFormat),
        full: date.format(this.afterTomorrowFormat),
      };
    }

    if (isTomorrow(date)) {
      return {
        timing: 'tomorrow',
        simple: date.format(this.todayOrTomorrowFormat),
        full: date.format(this.afterTomorrowFormat),
      };
    }

    return {
      timing: 'after-tomorrow',
      full: date.format(this.afterTomorrowFormat),
    };
  }
}
